/**
 * @file Шифрование и расшифровка данных с использованием AES-CBC и PBKDF2.
 *
 * Этот модуль предоставляет функции для безопасного шифрования и расшифровки бинарных данных (например, фрагментов файлов),
 * используя симметричное шифрование AES-CBC с 256-битным ключом.
 *
 * Основные особенности:
 * - Генерация криптографически безопасной соли (16 байт) для каждого шифрования
 * - Использование PBKDF2 (100 000 итераций и SHA-256) для вывода ключа из пароля
 * - Вектор инициализации (IV) должен быть передан извне и использоваться как при шифровании, так и при расшифровке
 * - Соль сохраняется в начале зашифрованного блока, чтобы её можно было извлечь при расшифровке
 *
 * Экспортируемые функции:
 * - `encrypt(chunk, password, iv): Promise<Blob>` — шифрует бинарные данные
 * - `decrypt(encryptedBytes, password, iv): Promise<ArrayBuffer>` — расшифровывает данные, зашифрованные с помощью `encrypt`
 *
 * Все криптографические операции выполняются через Web Crypto API.
 * Это существующий формат: AES-CBC сам по себе не проверяет подлинность данных;
 * успешная расшифровка и проверка длины не заменяют аутентифицированное шифрование.
 */

export const generate16RandomBytes = () =>
    crypto.getRandomValues(new Uint8Array(16)) // 16 байт для AES-CBC

export async function encrypt({
    chunk,
    password,
    iv,
}: {
    chunk: Uint8Array<ArrayBuffer>
    password: string
    iv: Uint8Array<ArrayBuffer>
}): Promise<Blob> {
    if (password === '') {
        return new Blob([chunk])
    }

    const salt = generate16RandomBytes()

    const keyMaterial = await getKeyMaterial(password)
    const key = await deriveKey(keyMaterial, salt)

    const encrypted = await crypto.subtle.encrypt(
        {
            name: 'AES-CBC',
            iv: iv,
        },
        key,
        chunk,
    )

    const saltedEncrypted = new Uint8Array(salt.length + encrypted.byteLength)
    saltedEncrypted.set(salt, 0)
    saltedEncrypted.set(new Uint8Array(encrypted), salt.length)

    return new Blob([saltedEncrypted])
}

async function getKeyMaterial(password: string): Promise<CryptoKey> {
    const encoder = new TextEncoder()

    return crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        'PBKDF2',
        false,
        ['deriveKey'],
    )
}

async function deriveKey(
    keyMaterial: CryptoKey,
    salt: Uint8Array<ArrayBuffer>,
): Promise<CryptoKey> {
    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100000,
            hash: 'SHA-256',
        },
        keyMaterial,
        {
            name: 'AES-CBC',
            length: 256,
        },
        false,
        ['encrypt', 'decrypt'],
    )
}

export async function decrypt({
    encryptedBytes,
    password,
    iv,
}: {
    encryptedBytes: Uint8Array<ArrayBuffer>
    password: string
    iv: Uint8Array<ArrayBuffer>
}): Promise<ArrayBuffer> {
    if (password === '') {
        return encryptedBytes
    }

    const salt = encryptedBytes.slice(0, 16)
    const encryptedData = encryptedBytes.slice(16)

    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveKey'],
    )

    const key = await crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100_000,
            hash: 'SHA-256',
        },
        keyMaterial,
        { name: 'AES-CBC', length: 256 },
        false,
        ['decrypt'],
    )

    return await crypto.subtle.decrypt(
        {
            name: 'AES-CBC',
            iv: iv,
        },
        key,
        encryptedData,
    )
}

export async function generateHmacBase64({
    key,
    message,
}: {
    key: string
    message: string
}): Promise<string> {
    const encoder = new TextEncoder()
    const keyData = encoder.encode(key)
    const messageData = encoder.encode(message)

    // Import the key
    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign'],
    )

    // Sign the message (generate HMAC)
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)

    // Convert ArrayBuffer to Base64
    return btoa(String.fromCharCode(...new Uint8Array(signature)))
}
